import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { User, Image } from 'lucide-react';
import { API } from '../config/api';

export default function Artists() {
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const fetchArtists = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/artists?page=${page}&limit=12`);
      setArtists(response.data.artists);
      setTotal(response.data.total);
    } catch {
      // Artists list fetch failed — silent
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    fetchArtists();
  }, [fetchArtists]);

  return (
    <div className="min-h-screen py-8 lg:py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-12">
          <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-4">Meet Our</p>
          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-[#F5F5F0]">
            Artists
          </h1>
          <p className="mt-4 text-[#A1A1AA]">
            {total} talented creators sharing their authentic work
          </p>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={`skeleton-${i}`} className="aspect-square skeleton" />
            ))}
          </div>
        ) : artists.length === 0 ? (
          <div className="text-center py-24">
            <p className="text-[#71717A] text-lg">No artists yet</p>
            <p className="text-[#71717A] mt-2">Be the first to join!</p>
            <Link to="/register" className="btn-primary inline-block mt-6">
              Become an Artist
            </Link>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="artists-grid">
              {artists.map(artist => (
                <Link
                  key={artist.id}
                  to={`/artist/${artist.id}`}
                  className="group bg-[#121212] border border-white/5 hover:border-white/20 transition-all p-6"
                  data-testid={`artist-card-${artist.id}`}
                >
                  <div className="w-20 h-20 mx-auto rounded-full bg-[#1A1A1A] flex items-center justify-center mb-6 group-hover:bg-[#D4AF37]/10 transition-colors">
                    {artist.profile_image ? (
                      <img 
                        src={artist.profile_image} 
                        alt={artist.name}
                        className="w-full h-full rounded-full object-cover"
                      />
                    ) : (
                      <User size={32} className="text-[#71717A] group-hover:text-[#D4AF37] transition-colors" />
                    )}
                  </div>
                  
                  <h3 className="font-serif text-xl text-[#F5F5F0] text-center">
                    {artist.name}
                  </h3>
                  
                  {artist.bio && (
                    <p className="mt-2 text-[#71717A] text-sm text-center line-clamp-2">
                      {artist.bio}
                    </p>
                  )}
                  
                  <div className="mt-6 flex items-center justify-center gap-2 text-[#A1A1AA]">
                    <Image size={16} />
                    <span className="text-sm">{artist.artwork_count || 0} works</span>
                  </div>
                </Link>
              ))}
            </div>

            {/* Pagination */}
            {total > 12 && (
              <div className="mt-12 flex justify-center gap-2">
                {[...Array(Math.ceil(total / 12))].map((_, i) => (
                  <button
                    key={`page-${i + 1}`}
                    onClick={() => setPage(i + 1)}
                    className={`w-10 h-10 flex items-center justify-center transition-colors ${
                      page === i + 1 
                        ? 'bg-[#D4AF37] text-[#0A0A0A]' 
                        : 'bg-[#121212] text-[#A1A1AA] hover:text-[#F5F5F0]'
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
